import * as Sentry from "@sentry/react";
import React from "react";
import {
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";

const environment = import.meta.env.REACT_APP_ENVIRONMENT;
const isProduction = environment === "production";
// Enabled everywhere except local dev machines. QA/feature branches (crealo-frontend-qa app)
// have REACT_APP_ENVIRONMENT undefined and are tagged "qa" so PR previews report JS errors too.
const sentryEnvironment = environment ?? "qa";

Sentry.init({
    enabled: environment !== "local",
    dsn: import.meta.env.REACT_APP_SENTRY_DSN,
    environment: sentryEnvironment,
    // To disable sending user data, uncomment the line below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
    // dataCollection: { userInfo: false },
    integrations: [
        // If you're using react router, use the integration for your react router version instead.
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration({ blockAllMedia: true, maskAllText: false }),
    ],
    // Performance traces: only sampled in production. Non-prod envs capture errors only.
    // Login-flow spans are always sampled in prod so we never miss a sign-in failure.
    tracesSampler: (samplingContext) => {
        if (!isProduction) {
            return 0;
        }
        if (samplingContext.attributes?.flow === "login" || samplingContext.name?.startsWith("auth.")) {
            return 1.0;
        }
        return 0.05;
    },
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ["localhost", /^\//, /^https:\/\/api\.crealo\.app/],
    // Session Replay: only recorded in production to avoid noise/storage cost on dev/staging.
    // In prod: 0% of clean sessions, 100% of sessions with an error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
    // Change this value if you want to have higher depth redux state details in Sentry issues, (eg: parse [Array] and [Object])
    normalizeDepth: 3,
    // Errors thrown by third-party widgets (Crisp chat, Hotjar) are not actionable on our side.
    denyUrls: [/client\.crisp\.chat/, /script\.hotjar\.com/],
    // Raised by Microsoft Outlook SafeLinks scanners crawling links from our emails, not by real users.
    // The second one comes from browsers with site storage blocked: the app (Cognito session included)
    // cannot run without localStorage, and there is no stack to act on.
    ignoreErrors: ["Object Not Found Matching Id", /Failed to read the 'localStorage' property from 'Window'/],
});

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
